// Local draft persistence for the whole workspace, so a reload, crash,
// closed tab, expired sign-in or the Google sign-in redirect never
// destroys unsaved work (DATA_INTEGRITY_INVESTIGATION.md, Issue 1).
//
// Product decision #2: a draft is never restored automatically — the
// app shows a "Restore unsaved work?" prompt.
//
// Pure except for the small storage helpers, which take the storage
// object as a parameter (window.localStorage in the app, a fake in
// tests) and never throw. Not yet wired into the app.
import { normalizeLoadedActivity } from "./activityLoad";
import { EMPTY_SESSION_META, isWorkspaceMeaningful } from "./workspaceState";
import { revisionOf } from "./recordRevision";

export const DRAFT_STORAGE_KEY = "tg:workspaceDraft:v1";
export const DRAFT_VERSION = 1;

// NP-4 Step 6 — a draft remembers the revision of each persisted record it
// was based on (`sessionRevision`, and `activityRevision` per tab), so a
// restored draft saves with that `baseRevision` and the server refuses it
// if the record has changed since (409 → the Save dialog's conflict
// choices). The fields are optional and additive: DRAFT_VERSION is NOT
// bumped, so drafts written before NP-4 Step 6 still restore. A revision
// only exists alongside its id; a draft with an id but no revision (older
// draft) restores with revision 0, the NP-4 legacy value — the server
// accepts it only if the record has not been saved since NP-4, otherwise
// the coach gets the conflict choices instead of an overwrite.
const draftRevision = (id, revision) => (id ? revisionOf({ revision }) : null);

const SAVE_MODES = new Set(["save-activity", "save-activity-new", "save-session", "save-session-new", "load"]);

// `tabs` are workspace entries with the live active tab already merged
// in (same input buildSessionSnapshot uses).
export function buildDraft({ tabs, activeTabId, sessionId, sessionRevision, sessionName, sessionMeta, pendingSaveMode }, now = Date.now()) {
  const list = tabs || [];
  const activeIndex = Math.max(0, list.findIndex((t) => t.tabId === activeTabId));
  return {
    version: DRAFT_VERSION,
    savedAt: now,
    sessionId: sessionId || null,
    sessionRevision: draftRevision(sessionId, sessionRevision),
    sessionName: sessionName || "",
    sessionMeta: { ...EMPTY_SESSION_META, ...(sessionMeta || {}) },
    activeIndex,
    pendingSaveMode: SAVE_MODES.has(pendingSaveMode) ? pendingSaveMode : null,
    tabs: list.map((t) => ({
      activityId: t.activityId || null,
      activityRevision: draftRevision(t.activityId, t.activityRevision),
      activity: t.activity,
      pitchMode: t.pitchMode,
      fieldColor: t.fieldColor,
      showDimensions: t.showDimensions,
      board: t.board,
      // Everything in a draft is already in current canonical coordinates.
      canonicalVersion: 2,
    })),
  };
}

export const serializeDraft = (draft) => JSON.stringify(draft);

// Returns a validated draft or null. Never throws.
export function parseDraft(raw) {
  if (typeof raw !== "string" || raw === "") return null;
  let d;
  try {
    d = JSON.parse(raw);
  } catch {
    return null;
  }
  if (!d || typeof d !== "object" || d.version !== DRAFT_VERSION) return null;
  if (!Array.isArray(d.tabs) || d.tabs.length === 0) return null;
  if (!d.tabs.every((t) => t && typeof t === "object" && t.activity && typeof t.activity === "object")) return null;
  const meta = d.sessionMeta && typeof d.sessionMeta === "object" ? d.sessionMeta : {};
  return {
    version: DRAFT_VERSION,
    savedAt: typeof d.savedAt === "number" ? d.savedAt : 0,
    sessionId: typeof d.sessionId === "string" && d.sessionId ? d.sessionId : null,
    sessionRevision: draftRevision(typeof d.sessionId === "string" && d.sessionId, d.sessionRevision),
    sessionName: typeof d.sessionName === "string" ? d.sessionName : "",
    sessionMeta: {
      ...EMPTY_SESSION_META,
      ...meta,
      offensivePrinciples: Array.isArray(meta.offensivePrinciples) ? meta.offensivePrinciples : [],
      defensivePrinciples: Array.isArray(meta.defensivePrinciples) ? meta.defensivePrinciples : [],
    },
    activeIndex: Number.isInteger(d.activeIndex) && d.activeIndex >= 0 && d.activeIndex < d.tabs.length ? d.activeIndex : 0,
    pendingSaveMode: SAVE_MODES.has(d.pendingSaveMode) ? d.pendingSaveMode : null,
    tabs: d.tabs,
  };
}

// Draft → workspace state ready for hydration. Every tab goes through
// the same normaliser as every other load path; tab ids are fresh.
export function draftToWorkspace(draft, makeTabId) {
  const tabs = draft.tabs.map((t) => {
    const n = normalizeLoadedActivity({
      ...t.activity,
      pitchMode: t.pitchMode,
      fieldColor: t.fieldColor,
      showDimensions: t.showDimensions,
      canonicalVersion: t.canonicalVersion || 2,
      ...(t.board || {}),
    });
    return {
      tabId: makeTabId(),
      activityId: t.activityId || null,
      activityRevision: draftRevision(t.activityId, t.activityRevision),
      ...n,
    };
  });
  return {
    tabs,
    activeTabId: tabs[Math.min(draft.activeIndex || 0, tabs.length - 1)].tabId,
    sessionId: draft.sessionId,
    sessionRevision: draftRevision(draft.sessionId, draft.sessionRevision),
    sessionName: draft.sessionName,
    sessionMeta: draft.sessionMeta,
    pendingSaveMode: draft.pendingSaveMode,
  };
}

// Only offer "Restore unsaved work?" when there is something worth restoring.
export function isDraftWorthRestoring(draft) {
  if (!draft) return false;
  return isWorkspaceMeaningful({
    tabs: draft.tabs,
    sessionName: draft.sessionName,
    sessionMeta: draft.sessionMeta,
  });
}

// ---- Storage helpers (never throw; storage may be unavailable) ----
export function saveDraft(storage, draft) {
  try {
    storage.setItem(DRAFT_STORAGE_KEY, serializeDraft(draft));
    return true;
  } catch {
    return false;
  }
}

export function loadDraft(storage) {
  try {
    return parseDraft(storage.getItem(DRAFT_STORAGE_KEY));
  } catch {
    return null;
  }
}

export function clearDraft(storage) {
  try {
    storage.removeItem(DRAFT_STORAGE_KEY);
    return true;
  } catch {
    return false;
  }
}
